/**
 * The dialogs the quotations screen opens: the New/Edit Quotation form (header
 * + a line-item editor), delete confirmation, revise, and send.
 *
 * Line items are free text here rather than a product picker — the seeded
 * catalogue (`md.product`) only has placeholder smoke-test rows, and a real
 * picker would need a `product_id` search UI that is a separate piece of
 * work. The server still recomputes every total from what is submitted.
 */
import { useEffect, useState } from 'react';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { money } from '../../utils/formatters.js';

/** Starting text for a new quotation — edit freely; a quotation cannot be saved without terms. */
const DEFAULT_TERMS = [
  '1. Prices are exclusive of applicable taxes unless stated otherwise.',
  '2. This quotation is valid until the date shown; prices may change after that.',
  '3. Payment is due as per the payment terms stated on this quotation.',
  '4. Delivery timelines start from receipt of the purchase order and advance payment, where applicable.',
  '5. Any change in scope or quantity may revise the quoted price.',
].join('\n');

const BLANK_HEADER = {
  account_id: '', opportunity_id: '', title: '', currency: 'USD',
  quote_date: new Date().toISOString().slice(0, 10), valid_until: '',
  payment_terms: '', discount_pct: '0', shipping_amount: '0', notes: '', terms_and_conditions: DEFAULT_TERMS,
};
const BLANK_ITEM = { description: '', quantity: '1', unit_price: '0', discount_pct: '0', tax_pct: '0', billing_cycle: '' };

function validate(header, items) {
  const errors = {};
  if (!header.account_id) errors.account_id = 'Choose an account.';
  if (!header.currency.trim() || header.currency.trim().length !== 3) errors.currency = 'A 3-letter currency code, e.g. USD.';
  if (!items.length) errors.items = 'Add at least one line item.';
  else if (items.some((it) => !it.description.trim())) errors.items = 'Every line needs a description.';
  else if (items.some((it) => Number(it.quantity) <= 0)) errors.items = 'Quantity must be greater than zero on every line.';
  else if (items.some((it) => Number(it.unit_price) < 0)) errors.items = 'Unit price cannot be negative.';
  if (!header.terms_and_conditions.trim()) errors.terms_and_conditions = 'Add the terms and conditions for this quotation.';
  if (header.valid_until && header.valid_until < header.quote_date) errors.valid_until = 'Cannot be before the quote date.';
  return errors;
}

function lineTotal(it) {
  const net = Number(it.quantity || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_pct || 0) / 100);
  return net * (1 + Number(it.tax_pct || 0) / 100);
}

/** Create a quotation, or edit one still in draft/rejected. `quotation` (a full GET /quotations/{id} record) switches the mode. */
export function QuotationFormModal({ open, onClose, onSubmit, busy, options, quotation }) {
  const [header, setHeader] = useState(BLANK_HEADER);
  const [items, setItems] = useState([{ ...BLANK_ITEM }]);
  const [errors, setErrors] = useState({});
  const editing = Boolean(quotation);

  useEffect(() => {
    if (!open) return;
    if (quotation) {
      setHeader({
        account_id: quotation.account?.id ?? '',
        opportunity_id: quotation.opportunity?.id ?? '',
        title: quotation.title ?? '',
        currency: quotation.currency ?? 'USD',
        quote_date: quotation.quote_date ?? BLANK_HEADER.quote_date,
        valid_until: quotation.valid_until ?? '',
        payment_terms: quotation.payment_terms ?? '',
        discount_pct: String(quotation.totals?.discount_pct ?? 0),
        shipping_amount: String(quotation.totals?.shipping_amount ?? 0),
        notes: quotation.notes ?? '',
        terms_and_conditions: quotation.terms_and_conditions ?? '',
      });
      setItems(
        (quotation.items ?? []).length
          ? quotation.items.map((it) => ({
              description: it.description ?? '', quantity: String(it.quantity), unit_price: String(it.unit_price),
              discount_pct: String(it.discount_pct ?? 0), tax_pct: String(it.tax_pct ?? 0), billing_cycle: it.billing_cycle ?? '',
            }))
          : [{ ...BLANK_ITEM }],
      );
    } else {
      setHeader(BLANK_HEADER);
      setItems([{ ...BLANK_ITEM }]);
    }
    setErrors({});
  }, [open, quotation]);

  const setField = (k) => (e) => {
    setHeader((h) => ({ ...h, [k]: e.target.value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };
  const setItem = (i, k) => (e) => {
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: e.target.value } : r)));
    setErrors((er) => (er.items ? { ...er, items: undefined } : er));
  };
  const addItem = () => setItems((rows) => [...rows, { ...BLANK_ITEM }]);
  const removeItem = (i) => setItems((rows) => rows.filter((_, idx) => idx !== i));

  const opportunitiesForAccount = header.account_id
    ? options.opportunities.filter((o) => o.accountId === header.account_id)
    : options.opportunities;

  const estimatedTotal = items.reduce((sum, it) => sum + lineTotal(it), 0)
    * (1 - Number(header.discount_pct || 0) / 100) + Number(header.shipping_amount || 0);

  const submit = (e) => {
    e.preventDefault();
    const nextErrors = validate(header, items);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const itemsPayload = items.map((it) => ({
      description: it.description.trim(),
      quantity: Number(it.quantity),
      unit_price: Number(it.unit_price),
      discount_pct: Number(it.discount_pct || 0),
      tax_pct: Number(it.tax_pct || 0),
      ...(it.billing_cycle ? { billing_cycle: it.billing_cycle } : {}),
    }));

    if (editing) {
      onSubmit({
        title: header.title || undefined,
        opportunity_id: header.opportunity_id || null,
        currency: header.currency.trim().toUpperCase(),
        quote_date: header.quote_date,
        valid_until: header.valid_until || null,
        payment_terms: header.payment_terms || undefined,
        discount_pct: Number(header.discount_pct || 0),
        shipping_amount: Number(header.shipping_amount || 0),
        notes: header.notes || undefined,
        terms_and_conditions: header.terms_and_conditions.trim(),
        items: itemsPayload,
      });
    } else {
      onSubmit({
        account_id: header.account_id,
        opportunity_id: header.opportunity_id || undefined,
        title: header.title || undefined,
        currency: header.currency.trim().toUpperCase(),
        quote_date: header.quote_date,
        valid_until: header.valid_until || undefined,
        payment_terms: header.payment_terms || undefined,
        discount_pct: Number(header.discount_pct || 0),
        shipping_amount: Number(header.shipping_amount || 0),
        notes: header.notes || undefined,
        terms_and_conditions: header.terms_and_conditions.trim(),
        items: itemsPayload,
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${quotation.quote_no}` : 'New Quotation'}
      size="lg"
      footer={(
        <>
          <div className="me-auto t-sm ink-3">Estimated total: <strong>{money(estimatedTotal, header.currency || 'USD')}</strong></div>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="quotationForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Create quotation'}
          </button>
        </>
      )}
    >
      <form id="quotationForm" onSubmit={submit} noValidate>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <Field label="Account" required error={errors.account_id}>
              <select className="form-select" value={header.account_id} onChange={setField('account_id')} disabled={editing}>
                <option value="">Choose an account…</option>
                {options.accounts.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Opportunity" help="Optional — links the quote to a deal.">
              <select className="form-select" value={header.opportunity_id} onChange={setField('opportunity_id')}>
                <option value="">No opportunity</option>
                {opportunitiesForAccount.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Title"><input className="form-control" value={header.title} onChange={setField('title')} placeholder="e.g. Annual renewal — 24 months" /></Field>
          </div>
          <div className="col-md-2">
            <Field label="Currency" required error={errors.currency}>
              <input className="form-control" maxLength={3} value={header.currency} onChange={(e) => setField('currency')({ target: { value: e.target.value.toUpperCase() } })} />
            </Field>
          </div>
          <div className="col-md-2">
            <Field label="Quote date"><input className="form-control" type="date" value={header.quote_date} onChange={setField('quote_date')} /></Field>
          </div>
          <div className="col-md-2">
            <Field label="Valid until" error={errors.valid_until}><input className="form-control" type="date" value={header.valid_until} onChange={setField('valid_until')} /></Field>
          </div>
          <div className="col-md-4">
            <Field label="Discount %" help="Applied on top of the line total.">
              <input className="form-control" type="number" min="0" max="100" step="0.5" value={header.discount_pct} onChange={setField('discount_pct')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Shipping"><input className="form-control" type="number" min="0" step="1" value={header.shipping_amount} onChange={setField('shipping_amount')} /></Field>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="eyebrow mb-0">Line items</div>
          <button type="button" className="btn btn-add btn-sm-x" onClick={addItem}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />Add line
          </button>
        </div>
        {errors.items && <div className="field-x-error mb-2">{errors.items}</div>}
        <div className="table-x-wrap mb-3">
          <table className="table-x is-dense">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: 90 }} className="text-end">Qty</th>
                <th style={{ width: 120 }} className="text-end">Unit price</th>
                <th style={{ width: 90 }} className="text-end">Disc. %</th>
                <th style={{ width: 90 }} className="text-end">Tax %</th>
                <th style={{ width: 110 }} className="text-end">Line total</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td><input className="form-control form-control-sm" value={it.description} onChange={setItem(i, 'description')} placeholder="e.g. Platform licence — annual" /></td>
                  <td><input className="form-control form-control-sm text-end" type="number" min="0" step="1" value={it.quantity} onChange={setItem(i, 'quantity')} /></td>
                  <td><input className="form-control form-control-sm text-end" type="number" min="0" step="1" value={it.unit_price} onChange={setItem(i, 'unit_price')} /></td>
                  <td><input className="form-control form-control-sm text-end" type="number" min="0" max="100" step="1" value={it.discount_pct} onChange={setItem(i, 'discount_pct')} /></td>
                  <td><input className="form-control form-control-sm text-end" type="number" min="0" step="0.5" value={it.tax_pct} onChange={setItem(i, 'tax_pct')} /></td>
                  <td className="text-end t-sm tabular">{money(lineTotal(it), header.currency || 'USD')}</td>
                  <td>
                    {items.length > 1 && (
                      <button type="button" className="btn-close btn-sm" aria-label="Remove line" onClick={() => removeItem(i)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Field label="Payment terms">
          <textarea className="form-control mb-3" rows={3} value={header.payment_terms} onChange={setField('payment_terms')} placeholder="e.g. Net 30; 50% advance, balance on delivery" />
        </Field>
        <Field label="Terms & conditions" required error={errors.terms_and_conditions}>
          <textarea className="form-control mb-3" rows={6} value={header.terms_and_conditions} onChange={setField('terms_and_conditions')} />
        </Field>
        <Field label="Notes"><textarea className="form-control" rows={2} value={header.notes} onChange={setField('notes')} /></Field>
      </form>
    </Modal>
  );
}

/** Delete, with the reason the database insists on. */
export function DeleteQuotationModal({ open, quotation, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete quotation"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-delete btn-sm-x" disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}>
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Deleting <strong>{quotation?.quote_no}</strong>. The row stays in the database with <code>deleted_at</code> set.
      </p>
      <Field label="Reason" required help="Written to the audit trail.">
        <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate of QT-2026-0091" autoFocus />
      </Field>
    </Modal>
  );
}

/** New version, sharing the same root. */
export function ReviseQuotationModal({ open, quotation, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  const [carryItems, setCarryItems] = useState(true);
  useEffect(() => { if (open) { setReason(''); setCarryItems(true); } }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Revise ${quotation?.quote_no ?? ''}`}
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" disabled={!reason.trim() || busy} onClick={() => onConfirm({ reason: reason.trim(), carry_items: carryItems })}>
            {busy ? <><ButtonSpinner />Creating…</> : 'Create revision'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Creates a new version {quotation ? `(v${(quotation.version_no ?? 1) + 1})` : ''} sharing this quote's number.
        The current version is marked superseded; this one starts editable again.
      </p>
      <Field label="Reason" required>
        <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer asked for revised terms" autoFocus />
      </Field>
      <div className="form-check mt-2">
        <input className="form-check-input" type="checkbox" id="carryItems" checked={carryItems} onChange={(e) => setCarryItems(e.target.checked)} />
        <label className="form-check-label t-sm" htmlFor="carryItems">Carry over the current line items</label>
      </div>
    </Modal>
  );
}

/** Send to the customer, optionally with the quotation attached as a PDF on the org letterhead. */
export function SendQuotationModal({ open, quotation, onClose, onConfirm, busy }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);
  useEffect(() => {
    if (open) { setAttachPdf(true); setTo(''); setSubject(quotation ? `Quotation ${quotation.quote_no}${quotation.title ? ` — ${quotation.title}` : ''}` : ''); }
  }, [open, quotation]);

  const emails = to.split(',').map((s) => s.trim()).filter(Boolean);
  const valid = emails.length > 0 && emails.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Send ${quotation?.quote_no ?? ''}`}
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" disabled={!valid || busy} onClick={() => onConfirm({ to: emails, subject, attach_pdf: attachPdf })}>
            {busy ? <><ButtonSpinner />Sending…</> : 'Send'}
          </button>
        </>
      )}
    >
      <Field label="To" required help="Comma-separated email addresses.">
        <input className="form-control" value={to} onChange={(e) => setTo(e.target.value)} placeholder="buyer@example.com" autoFocus />
      </Field>
      <Field label="Subject"><input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
      <div className="form-check">
        <input id="attachPdf" className="form-check-input" type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
        <label className="form-check-label" htmlFor="attachPdf">Attach the quotation as a PDF (on the company letterhead)</label>
      </div>
    </Modal>
  );
}

/** Approve or reject a pending step, from the caller's own queue entry. */
export function DecideApprovalModal({ open, request, onClose, onConfirm, busy }) {
  const [note, setNote] = useState('');
  const [decision, setDecision] = useState(null);
  useEffect(() => { if (open) { setNote(''); setDecision(null); } }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Decide on this quotation"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x"
            disabled={busy || !note.trim()}
            onClick={() => { setDecision('rejected'); onConfirm({ decision: 'rejected', note: note.trim() }); }}
          >
            {busy && decision === 'rejected' ? <><ButtonSpinner />Rejecting…</> : 'Reject'}
          </button>
          <button
            type="button" className="btn btn-save btn-sm-x"
            disabled={busy}
            onClick={() => { setDecision('approved'); onConfirm({ decision: 'approved', note: note.trim() || undefined }); }}
          >
            {busy && decision === 'approved' ? <><ButtonSpinner />Approving…</> : 'Approve'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">{request?.entity?.label}</p>
      <Field label="Note" required={false} help="A note is required when rejecting.">
        <textarea className="form-control" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}
